import React, { useState } from "react";
import {
  Box,
  Flex,
  Button,
  Text,
  VStack,
  Image,
  useColorModeValue,
  useBreakpointValue,
  useToast,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  GridItem,
  HStack,
  Icon,
  Heading,
  useDisclosure,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LogInIcon,
  LogOutIcon,
  Menu,
  MessageCircleIcon,
  Sun,
  Moon,
  Check,
  MenuIcon,
  BriefcaseBusiness,
} from "lucide-react";
import Cart from "../Cart/Cart";
import { useAppSelector } from "../../store/hooks/hooks";
import Logo from "../../assets/images/log.png";
import { useAuth } from "../../auth/AuthContext";

import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter } from "react-icons/fa";
import NotifyModal from "../Modals/NotifyModal";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { navItems } from "../SideBar/SideBar";
import { scrollPageToTop } from "../../utils/scrollPageToTop";


const hexToRgba = (hex: string, alpha: number) => {
  const cleaned = hex.replace("#", "");
  if (cleaned.length !== 6) return `rgba(100,116,139,${alpha})`;

  const bigint = parseInt(cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const MobileSideBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const cart = useAppSelector((state) => state.cart);
  const {
    isOpen: isCartOpen,
    onOpen: onCartOpen,
    onClose: onCartClose,
  } = useDisclosure();
  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const minWidth = useBreakpointValue({ base: "260px", md: "320px" });

  const { user, logout } = useAuth();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const openMenu = () => setIsMenuOpen(true);
  const closeMenu = () => setIsMenuOpen(false);
  const { theme, themeName, toggleTheme } = useCustomTheme();
  const [themeSaving, setThemeSaving] = useState(false);

  const isDark = themeName === "dark";

  const headerBorderBottom = isDark
    ? "1px solid rgba(255, 255, 255, 0.06)"
    : "1px solid #E2E8F0";

  const headerShadow = isDark ? "none" : "sm";

  const pageText = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;

  const shellBg = theme.gradients?.shellBg || theme.surface;
  const cardBg = theme.surface;

  const borderColor = theme.border;

  const panelShadow = theme.shadowMd;
  const cardShadow = theme.shadowSm;

  const activeBg = theme.gradients?.tabHikesSelected || theme.selected;

  const cardsBorderRadii = "8px";

  const handleThemeToggle = async () => {
    setThemeSaving(true);
    try {
      await toggleTheme();
      toast({
        title: `Theme changed to ${themeName === "dark" ? "light" : "dark"}`,
        status: "success",
        duration: 1800,
      });
    } catch (error) {
      console.error("Theme update failed:", error);
      toast({
        title: "Failed to update theme",
        status: "error",
        duration: 3000,
      });
    } finally {
      setThemeSaving(false);
    }
  };

  const renderContainedIcon = (
    IconComponent: React.ElementType,
    color: string,
    active = false,
    size = 16,
  ) => (
    <Flex
      w="30px"
      h="30px"
      borderRadius={cardsBorderRadii}
      align="center"
      justify="center"
      bg={hexToRgba(color, active ? 0.2 : 0.14)}
      border={`1px solid ${hexToRgba(color, active ? 0.34 : 0.22)}`}
      flexShrink={0}
      transition="all 0.2s ease"
      boxShadow={active ? theme.shadowSm : "none"}
    >
      <Icon
        as={IconComponent}
        boxSize={`${size}px`}
        color={color}
        fill="transparent"
        strokeWidth={2.2}
      />
    </Flex>
  );

  const handleAuthAction = async () => {
    if (!user) {
      closeMenu();
      navigate("/login");
      scrollPageToTop();
      return;
    }

    try {
      closeMenu();
      await logout();
      toast({
        title: "Sign Out",
        description: "You are now logged out!",
        status: "success",
        duration: 8000,
        isClosable: true,
      });
      navigate("/dashboard");
      scrollPageToTop();
    } catch (error) {
      console.error("Logout failed:", error);
      toast({
        title: "Logout failed",
        description: "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box bg={theme.background}>
      <Flex
        px={6}
        height="70px"
        alignItems="center"
        justifyContent="space-between"
        boxShadow={headerShadow}
        borderBottom={headerBorderBottom}
        bg={theme.background}
        color={theme.text}
        position="relative"
        zIndex={20}
      >
        {/* Logo */}
        <Flex cursor="pointer">
          <MenuIcon
            fontVariant={"ghost"}
            width="34px"
            height="34px"
            onClick={openMenu}
          />
        </Flex>

        <Flex
          ml="0px"
          flexWrap="nowrap"
          alignItems="center"
          onClick={() => navigate("/dashboard")}
        >
          <Image
            h="26px"
            src={Logo}
          />
          <Text
            ml="8px"
            fontSize="24px"
            fontWeight="bold"
            color={theme.text}
            userSelect="none"
            pointerEvents="none"
          >
            Pick
            <Text as="span" color="teal.500">
              Dengu
            </Text>
          </Text>
        </Flex>

        {/* Actions */}
        <Flex gap={2} align="center" position="relative">
          {!user && (
            <Button
              display={{ base: "none", md: "block" }}
              onClick={() => { navigate("/login"); scrollPageToTop(); }}
              leftIcon={<LogInIcon />}
              fontSize="sm"
              fontWeight="bold"
              color={theme.textOnPrimary}
              bgImage={theme.gradients.primaryButton}
              _hover={{ bgImage: theme.gradients.primaryButtonHover }}
            >
              Sign In
            </Button>
          )}

          <Box>
            <Button variant="ghost" mr="-10px" onClick={onCartOpen} h="34px">
              {renderContainedIcon(
                require("lucide-react").ShoppingCart,
                theme.brand?.electricBlue || theme.primary,
                false,
                16,
              )}
            </Button>
            <Flex
              position="absolute"
              onClick={onCartOpen}
              cursor="pointer"
              top="0px"
              left="35px"
              bgImage={theme.gradients.primaryButton}
              color={theme.textOnPrimary}
              borderRadius={cardsBorderRadii}
              alignItems="center"
              justifyContent="center"
              h="20px"
              w="20px"
              fontSize="10px"
              fontWeight="bold"
              transform="translateY(-10%)"
              boxShadow={theme.shadowSm}
            >
              {cart.items.length}
            </Flex>
          </Box>
        </Flex>
      </Flex>

      <Modal
        isOpen={isCartOpen}
        onClose={onCartClose}
        size="2xl"
        isCentered
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg={theme.overlay} />
        <ModalContent
          containerProps={{
            p: { base: 3, md: 6 },
          }}
          borderRadius={cardsBorderRadii}
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
          boxShadow={theme.shadowLg}
        >
          <ModalHeader color={theme.text}>Your Cart</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <Cart
              onCheckout={() => {
                onCartClose();
                navigate("/checkout");
                scrollPageToTop();
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Drawer placement="left" onClose={closeMenu} isOpen={isMenuOpen}>
        <DrawerOverlay bg={theme.overlay} backdropFilter="blur(4px)" />
        <DrawerContent
          pt="20px"
          bg={theme.surface}
          borderRight={`1px solid ${theme.border}`}
          boxShadow={theme.shadowLg}
        >
          <Flex
            ml="30px"
            flexWrap="nowrap"
            alignItems="center"
            cursor="pointer"
            onClick={() => {
              closeMenu();
              navigate("/dashboard");
              scrollPageToTop();
            }}
          >
            <Image
              h="26px"
              src={Logo}
            />
            <Text ml="8px" fontSize="24px" fontWeight="bold" color={pageText}>
              Pick
              <Text as="span" color="teal.500">
                Dengu
              </Text>
            </Text>
          </Flex>
          <DrawerCloseButton
            size="lg"
            mt="12px"
            ml="30px"
            color={theme.text}
            _hover={{ bg: theme.hover, color: theme.text }}
            _active={{ bg: theme.pressed || theme.hover }}
          />
          <DrawerBody>
            <VStack mt="30px" spacing={2} align="stretch">
              {navItems.map((item) => {
                const isActive = location.pathname.startsWith(item.path);

                return (
                  <Button
                    key={item.id}
                    onClick={() => {
                      closeMenu();
                      navigate(item.path);
                      scrollPageToTop();
                    }}
                    leftIcon={renderContainedIcon(
                      item.icon,
                      item.color,
                      isActive,
                    )}
                    justifyContent="flex-start"
                    variant="ghost"
                    fontWeight="600"
                    fontSize="sm"
                    height="46px"
                    borderRadius={cardsBorderRadii}
                    border={`1px solid ${
                      isActive ? hexToRgba(item.color, 0.24) : "transparent"
                    }`}
                    bg={isActive ? activeBg : "transparent"}
                    color={isActive ? pageText : mutedText}
                    boxShadow={isActive ? cardShadow : "none"}
                    _hover={{
                      bg: isActive ? activeBg : hoverBg,
                      color: pageText,
                      transform: "translateY(-1px)",
                    }}
                    _active={{ transform: "translateY(0)" }}
                    transition="all 0.2s ease"
                    marginTop="0"
                    px={3}
                  >
                    <Flex
                      width="100%"
                      justifyContent="space-between"
                      align="center"
                    >
                      <Text noOfLines={1}>{item.name}</Text>
                      {isActive && (
                        <Icon as={Check} boxSize={4} color={item.color} />
                      )}
                    </Flex>
                  </Button>
                );
              })}

              <Box
                mt={2}
                p={3}
                borderRadius={cardsBorderRadii}
                bg={cardBg}
                border={`1px solid ${borderColor}`}
                boxShadow={cardShadow}
              >
                <HStack justify="space-between" align="center">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="700" color={pageText}>
                      Theme
                    </Text>
                    <Text fontSize="xs" color={subtleText}>
                      {themeName === "dark" ? "Dark mode" : "Light mode"}
                    </Text>
                  </VStack>

                  <HStack spacing={2}>
                    <Flex
                      w="30px"
                      h="30px"
                      borderRadius={cardsBorderRadii}
                      align="center"
                      justify="center"
                      bg={
                        themeName === "dark"
                          ? hexToRgba("#8B5CF6", 0.14)
                          : hexToRgba("#F59E0B", 0.14)
                      }
                      border={`1px solid ${
                        themeName === "dark"
                          ? hexToRgba("#8B5CF6", 0.22)
                          : hexToRgba("#F59E0B", 0.22)
                      }`}
                    >
                      {themeName === "dark" ? (
                        <Moon size={16} color="#8B5CF6" />
                      ) : (
                        <Sun size={16} color="#F59E0B" />
                      )}
                    </Flex>

                    <Switch
                      isChecked={themeName === "dark"}
                      onChange={handleThemeToggle}
                      colorScheme="teal"
                      isDisabled={themeSaving}
                    />
                  </HStack>
                </HStack>
              </Box>

              <Button
                onClick={handleAuthAction}
                leftIcon={renderContainedIcon(
                  user ? LogOutIcon : LogInIcon,
                  user ? "#EF4444" : "#14B8A6",
                  false,
                )}
                justifyContent="flex-start"
                variant="ghost"
                fontWeight="600"
                fontSize="sm"
                borderRadius={cardsBorderRadii}
                height="46px"
                border="1px solid transparent"
                color={mutedText}
                _hover={{
                  bg: hoverBg,
                  color: pageText,
                  transform: "translateY(-1px)",
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.2s ease"
                marginTop="0"
              >
                <Text>{user ? "Log out" : "Log in"}</Text>
              </Button>

              <Button
                onClick={onOpen}
                justifyContent="flex-start"
                variant="ghost"
                fontWeight="600"
                fontSize="sm"
                borderRadius={cardsBorderRadii}
                height="46px"
                color={mutedText}
                _hover={{
                  bg: hoverBg,
                  color: pageText,
                  transform: "translateY(-1px)",
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.2s ease"
                marginTop="0"
              >
                {renderContainedIcon(MessageCircleIcon, "#06B6D4", false)}
                <Text ml="10px">Ask Us</Text>
              </Button>
              <Button
                onClick={() => { closeMenu(); navigate("/careers"); scrollPageToTop(); }}
                justifyContent="flex-start"
                variant="ghost"
                fontWeight="600"
                fontSize="sm"
                borderRadius={cardsBorderRadii}
                height="46px"
                color={mutedText}
                leftIcon={renderContainedIcon(
                  BriefcaseBusiness,
                  "#10897e",
                  location.pathname.startsWith("/careers")
                )}
                _hover={{
                  bg: hoverBg,
                  color: pageText,
                  transform: "translateY(-1px)",
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.2s ease"
                marginTop="0"
              >
                Careers
              </Button>

              <Box
                mt={4}
                p={4}
                borderRadius={cardsBorderRadii}
                bg={cardBg}
                border={`1px solid ${borderColor}`}
                boxShadow={cardShadow}
              >
                <HStack spacing={2} mb={4}>
                  <Flex
                    w="34px"
                    h="34px"
                    borderRadius={cardsBorderRadii}
                    align="center"
                    justify="center"
                    bg={hexToRgba("#0077B5", 0.12)}
                    border={`1px solid ${hexToRgba("#0077B5", 0.2)}`}
                    cursor="pointer"
                    transition="all 0.2s ease"
                    _hover={{ transform: "translateY(-1px)" }}
                  >
                    <Icon
                      as={FaLinkedin as React.ElementType}
                      boxSize={4}
                      color="#0077B5"
                    />
                  </Flex>

                  <Flex
                    w="34px"
                    h="34px"
                    borderRadius={cardsBorderRadii}
                    align="center"
                    justify="center"
                    bg={hexToRgba("#145DBF", 0.12)}
                    border={`1px solid ${hexToRgba("#145DBF", 0.2)}`}
                    cursor="pointer"
                    transition="all 0.2s ease"
                    _hover={{ transform: "translateY(-1px)" }}
                  >
                    <Icon
                      as={FaFacebook as React.ElementType}
                      boxSize={4}
                      color="#145DBF"
                    />
                  </Flex>

                  <Flex
                    w="34px"
                    h="34px"
                    borderRadius={cardsBorderRadii}
                    align="center"
                    justify="center"
                    bg={hexToRgba("#E4405F", 0.12)}
                    border={`1px solid ${hexToRgba("#E4405F", 0.2)}`}
                    cursor="pointer"
                    transition="all 0.2s ease"
                    _hover={{ transform: "translateY(-1px)" }}
                  >
                    <Icon
                      as={FaInstagram as React.ElementType}
                      boxSize={4}
                      color="#E4405F"
                    />
                  </Flex>

                  <Flex
                    w="34px"
                    h="34px"
                    borderRadius={cardsBorderRadii}
                    align="center"
                    justify="center"
                    bg={hexToRgba("#1DA1F2", 0.12)}
                    border={`1px solid ${hexToRgba("#1DA1F2", 0.2)}`}
                    cursor="pointer"
                    transition="all 0.2s ease"
                    _hover={{ transform: "translateY(-1px)" }}
                  >
                    <Icon
                      as={FaTwitter as React.ElementType}
                      boxSize={4}
                      color="#1DA1F2"
                    />
                  </Flex>
                </HStack>

                <GridItem>
                  <Heading
                    fontSize="12px"
                    fontWeight="700"
                    color={pageText}
                    mb={2}
                  >
                    About Us
                  </Heading>
                  <Text fontSize="sm" lineHeight="tall" color={subtleText}>
                    We bring you the best deals on electronics, fashion, and
                    more. Shop smart and earn rewards as you go with Dengu
                    Points!
                  </Text>
                  <Text fontSize="sm" lineHeight="tall" color={subtleText}>
                    <strong> PickDengu only earns when you earn. </strong>
                    We charge a small success fee on completed paid orders.
                    There are no listing fees, no monthly fees, and no hidden
                    setup costs. You keep most of your sale value, while
                    PickDengu helps you receive orders, payments, WhatsApp
                    updates, invoices, delivery coordination, and customer
                    visibility.
                  </Text>
                </GridItem>
              </Box>

              <NotifyModal isOpen={isOpen} onClose={onClose} />
            </VStack>
            <Flex />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default MobileSideBar;
